// Google Ads click-id persistence for server-side conversion claiming.
// gclid (web), wbraid (privacy-restricted web clicks from iOS), and gbraid
// (iOS app campaigns) only exist as query params on the ad-click landing URL;
// nothing sets a cookie for them unless gtag's conversion linker runs. We
// persist the raw values as first-party cookies named after the params, on
// the registrable domain (.pliability.com), and the API cookie sync reads the
// same names into segment_attributes at signup/sign-in.

import { getCookie } from "@/util/cookie";

const NINETY_DAYS_SECONDS = 90 * 24 * 60 * 60;

const CLICK_ID_PARAMS = ["gclid", "wbraid", "gbraid"] as const;

const getParam = (name: string): string => {
  if (typeof window === "undefined") return "";
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(name) || "";
  } catch {
    return "";
  }
};

const cookieDomain = (): string => {
  if (typeof window === "undefined") return "";
  const parts = window.location.hostname.split(".");
  if (parts.length < 2) return ""; // localhost etc. — host-only cookie
  return `.${parts.slice(-2).join(".")}`;
};

const setCookie = (name: string, value: string): void => {
  if (typeof document === "undefined") return;
  let cookie = `${name}=${value}; path=/; max-age=${NINETY_DAYS_SECONDS}; SameSite=Lax`;
  const domain = cookieDomain();
  if (domain) cookie += `; domain=${domain}`;
  document.cookie = cookie;
};

// Persist the click ids so they survive navigation from the ad-click landing
// URL through to signup/subscribe. Idempotent; safe to call on boot.
export const ensureGoogleAdsCookies = (): void => {
  if (typeof window === "undefined") return;

  for (const name of CLICK_ID_PARAMS) {
    const value = getParam(name);
    // A fresh click supersedes a stale cookie.
    if (value && getCookie(name) !== value) {
      setCookie(name, value);
    }
  }
};
